import {
  Box,
  Button,
  Card,
  Divider,
  Fade,
  Link,
  Paper,
  Popper,
  Typography,
  styled
} from '@mui/material'
import * as React from 'react'
import { colors } from '../../constants/colors'
import { HBox, VBox } from '../../elements/basic-elements'
import RepresentativePhoto from './representative-photo'
import donkey from '../../images/democrat.png'
import EmailIcon from '../../images/icons/email-icon'
import PhoneIcon from '../../images/icons/phone-icon'
import elephant from '../../images/republican.png'
import { PillSmall } from '../../styles/styled-components/inputs/pills'
import { locationState } from '../../context/location-context'
import { authState } from '../../state/auth-state'

export type RepCardData = {
  action?: string
  name?: string
  photo?: string
  party?: string
  title?: string
  websites?: { type: string; url: string }[]
  script?: Queries.Maybe<string> | undefined
  emails?: { email: string; type?: string }[]
  phones?: { phone: string; type?: string }[]
}

export const toParagraphs = (script: string) => {
  return script.split(/\n+/)
}

const RepCard: React.FC<RepCardData> = ({
  action,
  name,
  photo,
  party,
  title,
  script,
  emails,
  phones
}) => {
  const { account } = authState()
  const partyString = party?.toLowerCase()
  const isDemocrat = partyString === 'democratic'
  const isRepublican = partyString === 'republican'
  const partyImage = isDemocrat ? donkey : isRepublican ? elephant : undefined
  const { locationData } = locationState()

  const emailPopperRef = React.useRef(null)
  const [emailPopperAnchorEl, setEmailPopperAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleEmailPopperClick = (event: React.MouseEvent<HTMLElement>) => {
    setEmailPopperAnchorEl(emailPopperAnchorEl ? null : event.currentTarget)
  }
  const emailOpen = Boolean(emailPopperAnchorEl)
  const emailId = emailOpen ? 'email-popper' : undefined

  // TODO: these popper refs and the hoops being jumped through to make them
  // work seem suboptimal. Needs a refactor
  const phonePopperRef = React.useRef(null)
  const [phonePopperAnchorEl, setPhonePopperAnchorEl] = React.useState<null | HTMLElement>(null)
  const handleCallPopperClick = (event: React.MouseEvent<HTMLElement>) => {
    setPhonePopperAnchorEl(phonePopperAnchorEl ? null : event.currentTarget)
  }
  const phoneOpen = Boolean(phonePopperAnchorEl)
  const phoneId = phoneOpen ? 'phone-popper' : undefined

  // Close the email popper when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emailPopperAnchorEl &&
        emailPopperRef.current &&
        !emailPopperRef.current.contains(event.target as Node)
      ) {
        setEmailPopperAnchorEl(null)
      }
      if (
        phonePopperAnchorEl &&
        phonePopperRef.current &&
        !phonePopperRef.current.contains(event.target as Node)
      ) {
        setPhonePopperAnchorEl(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [emailPopperRef.current, phonePopperRef.current])

  const preparedScript = replaceScriptPlaceholders(script, {
    name: account?.user.profile.public.name || '(Your Name)',
    city: locationData?.city?.long_name || '(Your City)',
    representative: name
  })

  return (
    <RepCardEl>
      <VBox alignItems="center">
        <RepresentativePhoto size={8} photo={photo} />
        <Typography color="textPrimary" variant="h4" sx={{ mt: 2.5, mb: 1.5 }}>
          {name}
        </Typography>
        <HBox gap={1}>
          {party && (
            <PillSmall
              sx={{
                backgroundColor: theme =>
                  isDemocrat
                    ? colors.BLUE_DEMOCRAT_100
                    : isRepublican
                      ? colors.RED_REPUBLICAN_100
                      : theme.palette.info.main,
                display: 'flex',
                gap: 0.5,
                alignItems: 'center'
              }}
            >
              {partyImage && <img src={partyImage} width={16} height={16} />}
              <Typography
                variant="body2"
                color={isRepublican || isDemocrat ? 'primary.contrastText' : 'textPrimary'}
              >
                {party}
              </Typography>
            </PillSmall>
          )}
          {title && (
            <PillSmall
              sx={{
                backgroundColor: theme =>
                  theme.palette.mode == 'dark'
                    ? theme.palette.secondary.main
                    : colors.GLACIER_BLUE_70
              }}
            >
              <Typography variant="body2" color="textPrimary">
                {title}
              </Typography>
            </PillSmall>
          )}
        </HBox>
      </VBox>
      <VBox flex={1} gap={3}>
        {script && (
          <Box>
            <ScriptText variant="subtitle2">Script:</ScriptText>
            <Typography variant="body2" color="textPrimary">
              {toParagraphs(preparedScript.replace('[CALLING]', 'calling')).map(p => (
                <>
                  {p}
                  <br />
                  <br />
                </>
              ))}
            </Typography>
            {phones && phones.length > 0 && (
              <React.Fragment>
                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  disableElevation
                  aria-describedby={phoneId}
                  startIcon={<PhoneIcon />}
                  onClick={handleCallPopperClick}
                  onBlur={() => setPhonePopperAnchorEl(null)}
                >
                  Be Heard!
                </Button>
                <Popper
                  id={phoneId}
                  open={phoneOpen}
                  anchorEl={phonePopperAnchorEl}
                  placement="top"
                  disablePortal
                  transition
                  ref={phonePopperRef}
                >
                  {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={350}>
                      {/* @ts-expect-error: existing type issue */}
                      <StyledPaper width="100%" elevation={3}>
                        <Box px={4}>
                          {phones?.map((phone, i) => {
                            return (
                              <HBox alignItems={'center'} p={2} gap={1}>
                                {phone.type && <Typography>{phone.type}:</Typography>}
                                <StyledLink href={`tel:${phone.phone}`}>
                                  <Typography>{phone.phone}</Typography>
                                </StyledLink>
                                {i !== phones.length - 1 && <Divider />}
                              </HBox>
                            )
                          })}
                        </Box>
                      </StyledPaper>
                    </Fade>
                  )}
                </Popper>
              </React.Fragment>
            )}
            {emails && (
              <React.Fragment>
                <Box p={3}>
                  <Divider />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="body2" color="textPrimary">
                    If there is no response, you can send an email instead.
                  </Typography>
                </Box>
              </React.Fragment>
            )}
          </Box>
        )}
      </VBox>
      <VBox gap={1} justifyContent="center">
        {emails && emails.length > 0 && (
          <React.Fragment>
            <Button
              variant="contained"
              color={'secondary'}
              disableElevation
              aria-describedby={emailId}
              startIcon={<EmailIcon />}
              onClick={handleEmailPopperClick}
            >
              Send Email
            </Button>

            <Popper
              id={emailId}
              open={emailOpen}
              anchorEl={emailPopperAnchorEl}
              placement="top"
              disablePortal
              transition
              ref={emailPopperRef}
            >
              {({ TransitionProps }) => (
                <Fade {...TransitionProps} timeout={350}>
                  {/* @ts-expect-error: existing type issue */}
                  <StyledPaper width="100%" elevation={3}>
                    <Box px={4}>
                      {emails?.map((email, i) => {
                        const subject = `${action}`
                        const body = preparedScript.replace('[CALLING]', 'emailing')
                        return (
                          <HBox gap={1} p={2}>
                            {email.type && <Typography>{email.type}:</Typography>}
                            <Link
                              href={`mailto:${email.email}?subject=${subject}&body=${body}`}
                              color="inherit"
                            >
                              <Typography>{email.email}</Typography>
                            </Link>
                            {i !== emails.length - 1 && <Divider />}
                          </HBox>
                        )
                      })}
                    </Box>
                  </StyledPaper>
                </Fade>
              )}
            </Popper>
          </React.Fragment>
        )}
      </VBox>
    </RepCardEl>
  )
}

const replaceScriptPlaceholders = (
  script: string,
  replacements: { name?: string; city?: string; representative?: string }
) => {
  const patterns: Record<string, RegExp> = {
    name: /\[NAME\]/g,
    city: /\[DISTRICT\/CITY\]/g,
    representative: /\[REP_NAME\]/g
  }

  for (const key in patterns) {
    script = script.replace(patterns[key], replacements[key] ?? '')
  }
  return script
}

const RepCardEl = styled(Card)`
  flex: 1;
  padding: ${({ theme }) => theme.spacing(2.5)};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(2.5)};
  overflow: scroll;

  ${({ theme }) => `
    background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_80 : colors.WHITE};
  `}
`

const ScriptText = styled(Typography)`
  color: ${colors.BLACK_50};
  text-transform: uppercase;
  letter-spacing: 0.36px;
  line-height: 1.05;
  margin-bottom: ${({ theme }) => theme.spacing(1.25)};
`

const StyledPaper = styled(Paper)`
  ${({ theme }) => `
      background: ${theme.palette.mode == 'dark' ? colors.DARK_MODE_50 : colors.GEORGIAN_BAY_10}
    `}
`

const StyledLink = styled(Link)`
  text-decoration: none;
  ${({ theme }) => `
      color: ${theme.palette.mode == 'dark' ? colors.WHITE : colors.BLACK_100}
    `}
`

export default RepCard
